import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuSeparator, DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { PageHeader, Disclaimer } from "@/components/Common";
import { Plus, MoreHorizontal, Eye, Pencil, Music, Send, XCircle } from "lucide-react";
import { LICENSE_REQUEST_STATUSES } from "@/lib/constants";
import { SEARCH_CATEGORIES } from "@/lib/lists";
import { SearchWithCategory, matchesSearch } from "@/components/Controlled";
import { useAuth, canWrite } from "@/lib/auth";
import { toast } from "sonner";

// MP6 — License Requests list:
//  • 15 MP6 columns + searchable + sortable + status filter
//  • Quick actions: View, Edit, Match to Track, Create Submission Draft, Close Request
//  • Internal alpha only — no public intake.

function statusCls(s) {
  if (s === "Approved for Submission") return "status-client";
  if (["Declined", "Closed"].includes(s)) return "status-incomplete";
  if (["Need More Information", "In Review", "Pending Approval"].includes(s)) return "status-needs-review";
  if (s === "Matched to Track") return "status-internally";
  if (s === "New") return "status-internally";
  return "status-incomplete";
}

const SORTABLE = {
  request_id: (a, b) => (a.request_id || "").localeCompare(b.request_id || ""),
  date_received: (a, b) => (a.date_received || "").localeCompare(b.date_received || ""),
  deadline: (a, b) => (a.deadline || "").localeCompare(b.deadline || ""),
  project_title: (a, b) => (a.project_title || "").localeCompare(b.project_title || ""),
  requester_name: (a, b) => (a.requester_name || "").localeCompare(b.requester_name || ""),
  request_status: (a, b) => (a.request_status || "").localeCompare(b.request_status || ""),
};

export default function LicenseRequestsList() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const writeAccess = canWrite(user);
  const [items, setItems] = useState([]);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [searchCat, setSearchCat] = useState("all");
  const [loading, setLoading] = useState(true);
  const [sortKey, setSortKey] = useState("date_received");
  const [sortDir, setSortDir] = useState("desc");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (status !== "all") params.request_status = status;
      const r = await api.get("/license-requests", { params });
      setItems(r.data || []);
    } catch (e) { toast.error(errMsg(e)); }
    finally { setLoading(false); }
  }

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  const visible = useMemo(() => {
    let rows = items;
    const anyFields = ["request_title","requester_name","requester_company","project_title","project_type","genre_requested","mood_requested","budget_range","deadline","request_status","assigned_reviewer","requested_track_title","notes"];
    rows = rows.filter((r) => matchesSearch(r, q, searchCat, licenseFieldOf, anyFields));
    const cmp = SORTABLE[sortKey] || SORTABLE.date_received;
    rows = [...rows].sort((a, b) => sortDir === "asc" ? cmp(a, b) : cmp(b, a));
    return rows;
  }, [items, q, searchCat, sortKey, sortDir]);

  function toggleSort(k) {
    if (sortKey === k) setSortDir(sortDir === "asc" ? "desc" : "asc");
    else { setSortKey(k); setSortDir("asc"); }
  }

  async function closeRequest(r) {
    if (!window.confirm(`Close request ${r.request_id}?`)) return;
    try {
      await api.put(`/license-requests/${r.id}`, { ...r, request_status: "Closed" });
      toast.success("Request closed");
      load();
    } catch (e) { toast.error(errMsg(e)); }
  }

  return (
    <div data-testid="license-requests-page">
      <PageHeader
        title="License Requests"
        subtitle="Internal intake and review workflow for music use requests."
        actions={writeAccess ? (
          <Button onClick={() => navigate("/license-requests/new")} data-testid="license-new-button" style={{ background: "hsl(347 72% 22%)" }}>
            <Plus className="w-4 h-4 mr-2" />New Request
          </Button>
        ) : (
          <span className="text-[11px] uppercase tracking-wide text-muted-foreground" data-testid="license-readonly-badge">Read-only access</span>
        )}
      />

      <Card className="mb-4">
        <CardContent className="p-4 space-y-3">
          <SearchWithCategory
            query={q}
            onQueryChange={setQ}
            category={searchCat}
            onCategoryChange={setSearchCat}
            categories={SEARCH_CATEGORIES.license}
            placeholder="Search license requests…"
            testidPrefix="license"
          />
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[220px]">
              <label className="text-xs text-muted-foreground">Status</label>
              <Select value={status} onValueChange={setStatus}>
                <SelectTrigger data-testid="license-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {LICENSE_REQUEST_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <span className="text-xs text-muted-foreground self-end pb-2 ml-auto" data-testid="license-count">
              {visible.length} of {items.length}
            </span>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0 overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTH onClick={() => toggleSort("request_id")} active={sortKey === "request_id"} dir={sortDir}>Request ID</SortableTH>
                <SortableTH onClick={() => toggleSort("date_received")} active={sortKey === "date_received"} dir={sortDir}>Date received</SortableTH>
                <SortableTH onClick={() => toggleSort("requester_name")} active={sortKey === "requester_name"} dir={sortDir}>Requester</SortableTH>
                <TableHead>Company</TableHead>
                <SortableTH onClick={() => toggleSort("project_title")} active={sortKey === "project_title"} dir={sortDir}>Project</SortableTH>
                <TableHead>Project type</TableHead>
                <TableHead>Usage type</TableHead>
                <TableHead>Requested track</TableHead>
                <TableHead>Budget</TableHead>
                <SortableTH onClick={() => toggleSort("deadline")} active={sortKey === "deadline"} dir={sortDir}>Deadline</SortableTH>
                <SortableTH onClick={() => toggleSort("request_status")} active={sortKey === "request_status"} dir={sortDir}>Status</SortableTH>
                <TableHead>Reviewer</TableHead>
                <TableHead>Linked submission</TableHead>
                <TableHead className="w-[60px] sticky right-0 bg-card"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">Loading…</TableCell>
                </TableRow>
              )}
              {!loading && visible.length === 0 && (
                <TableRow>
                  <TableCell colSpan={14} className="text-center py-10 text-muted-foreground">No license requests match. Create one with the New Request button.</TableCell>
                </TableRow>
              )}
              {visible.map(r => (
                <TableRow
                  key={r.id}
                  className="cursor-pointer hover:bg-secondary/40"
                  onClick={() => navigate(`/license-requests/${r.id}`)}
                  data-testid={`license-row-${r.id}`}
                >
                  <TableCell className="font-mono text-xs whitespace-nowrap">{r.request_id}</TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground whitespace-nowrap">{(r.date_received || "").slice(0, 10) || "—"}</TableCell>
                  <TableCell className="max-w-[140px] truncate">{r.requester_name || "—"}</TableCell>
                  <TableCell className="max-w-[140px] truncate text-sm text-muted-foreground">{r.requester_company || "—"}</TableCell>
                  <TableCell className="max-w-[180px] truncate font-medium">{r.project_title || "—"}</TableCell>
                  <TableCell className="text-xs">{r.project_type || "—"}</TableCell>
                  <TableCell className="text-xs">{r.usage_type || "—"}</TableCell>
                  <TableCell className="max-w-[180px] truncate text-sm">
                    {r.requested_track_title || (r.requested_track_id ? <span className="font-mono text-xs">{r.requested_track_id.slice(0, 8)}…</span> : "—")}
                  </TableCell>
                  <TableCell className="text-xs">{r.budget_range || "—"}</TableCell>
                  <TableCell className="font-mono text-[11px] whitespace-nowrap">{r.deadline || "—"}</TableCell>
                  <TableCell><span className={`inline-flex px-2 py-1 rounded text-xs whitespace-nowrap ${statusCls(r.request_status)}`}>{r.request_status}</span></TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-[140px] truncate">{r.assigned_reviewer || "—"}</TableCell>
                  <TableCell className="font-mono text-[11px] text-muted-foreground">{r.linked_submission_id ? r.linked_submission_id.slice(0, 8) + "…" : "—"}</TableCell>
                  <TableCell className="sticky right-0 bg-card" onClick={(e) => e.stopPropagation()}>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8" data-testid={`license-actions-${r.id}`} aria-label="Request actions">
                          <MoreHorizontal className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-[220px]">
                        <DropdownMenuLabel>Quick actions</DropdownMenuLabel>
                        <DropdownMenuItem onClick={() => navigate(`/license-requests/${r.id}`)} data-testid={`license-action-view-${r.id}`}>
                          <Eye className="w-4 h-4 mr-2" />View
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/license-requests/${r.id}`)} data-testid={`license-action-edit-${r.id}`}>
                          <Pencil className="w-4 h-4 mr-2" />Edit
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/license-requests/${r.id}?match=1`)} data-testid={`license-action-match-${r.id}`}>
                          <Music className="w-4 h-4 mr-2" />Match to Track
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => navigate(`/submissions/new?request=${r.id}${r.requested_track_id ? `&track=${r.requested_track_id}` : ""}`)} data-testid={`license-action-submission-${r.id}`}>
                          <Send className="w-4 h-4 mr-2" />Create Submission Draft
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        <DropdownMenuItem onClick={() => closeRequest(r)} data-testid={`license-action-close-${r.id}`}>
                          <XCircle className="w-4 h-4 mr-2" />Close Request
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Disclaimer>
        License request handling is for internal workflow and compliance support only · Not legal advice · Rights status must be verified before external submission.
      </Disclaimer>
    </div>
  );
}

function SortableTH({ children, onClick, active, dir }) {
  return (
    <TableHead onClick={onClick} className="cursor-pointer select-none">
      <span className="inline-flex items-center gap-1">
        {children}
        {active && <span className="text-[10px] text-muted-foreground">{dir === "asc" ? "▲" : "▼"}</span>}
      </span>
    </TableHead>
  );
}

function licenseFieldOf(r, fieldId) {
  switch (fieldId) {
    case "request_title":      return r.request_title || r.request_id;
    case "requester_name":     return r.requester_name;
    case "requester_company":  return r.requester_company;
    case "project_title":      return r.project_title;
    case "project_type":       return r.project_type;
    case "genre_requested":    return r.genre_requested;
    case "mood_requested":     return Array.isArray(r.mood_requested) ? r.mood_requested.join(", ") : (r.mood_requested || "");
    case "budget_range":       return r.budget_range;
    case "deadline":           return r.deadline;
    case "request_status":     return r.request_status;
    case "assigned_reviewer":  return r.assigned_reviewer;
    case "requested_track_title": return r.requested_track_title || r.requested_track_id;
    case "notes":              return [r.internal_notes, r.review_notes, r.intake_summary].filter(Boolean).join(" · ");
    default: return "";
  }
}
